import React from 'react';
import {
  hereNow,
  subscribeToRoom,
  fetchRoomHistory,
  addEventListener,
  removeEventListenerByScope,
  unsubscribeToRoom,
  getUserMetaData,
} from '../helpers/pubNubFunctions';

function reducer(state, action) {
  switch (action.type) {
    case 'add_message':
      if (state.find(m => m.timetoken === action.payload.timetoken)) {
        return state;
      } else {
        return [...state, action.payload];
      }
    case 'delete_message':
      return state.filter(message => message.timetoken != action.payload);
    case 'populate':
      return [...action.payload];
    case 'add_action':
      return state.map(message => {
        if (message.timetoken == action.payload.messageTimetoken) {
          if (!message.actions) {
            message.actions = {};
          }
          if (!message.actions[action.payload.type]) {
            message.actions[action.payload.type] = {};
          }
          if (!message.actions[action.payload.type][action.payload.value]) {
            message.actions[action.payload.type][action.payload.value] = [];
          }
          message.actions[action.payload.type][action.payload.value] = [
            ...message.actions[action.payload.type][action.payload.value],
            {
              uuid: action.payload.uuid,
              actionTimetoken: action.payload.actionTimetoken,
            },
          ];
        }
        return message;
      });
    case 'remove_action':
      return state.map(message => {
        if (message.timetoken == action.payload.messageTimetoken) {
          message.actions[action.payload.type][action.payload.value] = message.actions[action.payload.type][
            action.payload.value
          ].filter(act => {
            return act.actionTimetoken != action.payload.actionTimetoken;
          });
        }
        return message;
      });
    default:
      throw new Error(`No handler for dispatch action type ${action.type} in usePubNubMessages`);
  }
}

export default function usePubNubMessages(room, updateChannel = false) {
  const [messages, dispatch] = React.useReducer(reducer, []);

  // Eggscribe
  // Fetch message history
  // Listen for messages and Modmessages
  // Cleanup listeners / pubnub
  React.useEffect(() => {
    subscribeToRoom(room, false);

    fetchRoomHistory(room, (status, response) => {
      if (status.statusCode === 404) {
        // no history.
        return;
      } else if (status.error) {
        console.error(status?.errorData?.message);
        return;
      } else {
        if (response.channels[room]) {
          dispatch({
            type: 'populate',
            payload: response.channels[room],
          });
        }
      }
    });

    addEventListener(
      PubNubCenter.ON_MESSAGE,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_message',
            payload: {
              channel: data.channel,
              timetoken: data.timetoken,
              message: data.message,
              user: data.user,
            },
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_REMOVE_MESSAGE,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'delete_message',
            payload: data.message.text,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_POLL_END_EARLY,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_action',
            payload: data.data,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_QA_ANSWER,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_action',
            payload: data.data,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_QA_POSTED,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_action',
            payload: data.data,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_QA_ANSWERED,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_action',
            payload: data.data,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_MESSAGE_FLAGGED,
      (event, data) => {
        if (data?.channel == room) {
          dispatch({
            type: 'add_action',
            payload: data.data,
          });
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_QA_UPVOTED,
      (event, data) => {
        if (data?.channel == room) {
          if (data.event == 'removed') {
            dispatch({
              type: 'remove_action',
              payload: data.data,
            });
          } else {
            dispatch({
              type: 'add_action',
              payload: data.data,
            });
          }
        }
      },
      'chat_pane_message_listeners',
    );

    addEventListener(
      PubNubCenter.ON_PRESENCE,
      (event, data) => {
        if (
          !CrowdHub_App.CONFIG.HIDE_USER_JOIN_CHAT &&
          updateChannel &&
          data?.action === 'join' &&
          data?.channel == updateChannel
        ) {
          messageUserJoined(data);
        }
      },
      'chat_pane_message_listeners',
    );

    function messageUserJoined(data) {
      getUserMetaData(data.uuid, (status, result) => {
        const aUser: JUNOCustomUser = new CustomUser();
        aUser.initialize({
          user_firstname: result.data?.custom?.firstname,
          user_lastname: result.data?.custom?.lastname,
          user_avatar: result.data?.custom?.avatar,
          user_id: result.data?.externalId,
          chat_room: result.data?.id,
        });
        let aDate = moment(new Date());
        let aHumanStartDate = aDate.format('h:mm a');
        let names = `${aUser.Firstname} ${CrowdHub_App.CONFIG.SHOW_USER_LASTNAME ? aUser.Lastname : ''}`;
        if (names.length > 23) {
          names = names.substring(0, 20) + '...';
        }
        let messageText = `${names} has joined - ${aHumanStartDate}`;
        dispatch({
          type: 'add_message',
          payload: {
            channel: data.channel,
            timetoken: data.timetoken,
            message: {
              text: messageText,
              user: aUser,
              type: 'update',
            },
          },
        });
      });
    }

    addEventListener(
      PubNubCenter.PRESENCE_INTERVAL,
      (event, data) => {
        if (!CrowdHub_App.CONFIG.HIDE_USER_JOIN_CHAT && updateChannel && data?.channel == updateChannel) {
          if (data.join?.length > 1) {
            getUserMetaData(data.join[0], (status, result) => {
              const aUser: JUNOCustomUser = new CustomUser();
              aUser.initialize({
                user_firstname: result.data?.custom?.firstname,
                user_lastname: result.data?.custom?.lastname,
                user_avatar: result.data?.custom?.avatar,
                user_id: result.data?.externalId,
                chat_room: result.data?.id,
              });
              let names = aUser.Firstname;
              if (names.length > 20) {
                names = names.substring(0, 17) + '...';
              }
              let messageText = `${names} and ${data.join.length} other people joined`;
              dispatch({
                type: 'add_message',
                payload: {
                  channel: data.channel,
                  timetoken: data.timetoken,
                  message: {
                    text: messageText,
                    user: aUser,
                    type: 'update',
                  },
                },
              });
            });
          }
        } else if (!CrowdHub_App.CONFIG.HIDE_USER_JOIN_CHAT && data.join?.length === 1) {
          messageUserJoined(data);
        }
      },
      'chat_pane_message_listeners',
    );

    return () => {
      removeEventListenerByScope(PubNubCenter.ON_MESSAGE, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_REMOVE_MESSAGE, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_POLL_END_EARLY, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_QA_ANSWER, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_QA_ANSWERED, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_QA_POSTED, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_MESSAGE_FLAGGED, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_QA_UPVOTED, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.ON_PRESENCE, 'chat_pane_message_listeners');
      removeEventListenerByScope(PubNubCenter.PRESENCE_INTERVAL, 'chat_pane_message_listeners');
      unsubscribeToRoom(room);
    };
  }, []);

  return [messages, dispatch];
}
